import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Grid,
  Autocomplete,
  CircularProgress,
  Alert
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { wishlistService, masterDataService } from '../../services/bookService';
import { currencyService } from '../../services/currencyService';

function WishDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isNew = id === 'new';

  const [formData, setFormData] = useState({
    brand: '',
    code: '',
    name: '',
    series: '',
    type: '',
    scale: '',
    imageUrl: '',
    price: 0,
    priceCurrency: 'USD',
    note: '-'
  });

  const [masterData, setMasterData] = useState({
    brands: [],
    series: [],
    types: [],
    scales: [],
    sources: []
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [userCurrency, setUserCurrency] = useState('USD');

  // Helper function to deduplicate arrays
  const deduplicateArray = (arr) => {
    if (!Array.isArray(arr)) return [];
    return [...new Set(arr)];
  };

  useEffect(() => {
    loadMasterData();
    loadUserCurrency();
    if (!isNew && id && id !== 'new') {
      loadWishItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  // Update default currency when user currency is loaded
  useEffect(() => {
    if (userCurrency && userCurrency !== 'USD') {
      setFormData(prev => ({
        ...prev,
        priceCurrency: userCurrency
      }));
    }
  }, [userCurrency]);

  async function loadMasterData() {
    try {
      const data = await masterDataService.getMasterData();
      setMasterData(data);
    } catch (error) {
      console.error('Failed to load master data:', error);
    }
  }

  async function loadUserCurrency() {
    if (!currentUser?.uid) return;
    
    try {
      const currency = await currencyService.getUserCurrency(currentUser.uid);
      setUserCurrency(currency);
    } catch (error) {
      console.error('Error loading user currency:', error);
    }
  }

  async function convertPriceToUserCurrency(amount, fromCurrency) {
    if (!amount || amount === 0) return 0;
    if (fromCurrency === userCurrency) return amount;
    
    try {
      const converted = await currencyService.convertCurrency(amount, fromCurrency, userCurrency);
      return converted;
    } catch (error) {
      console.error('Error converting currency:', error);
      return amount; // Return original amount if conversion fails
    }
  }

  async function loadWishItem() {
    try {
      setLoading(true);
      const wishItem = await wishlistService.getWishItemById(id);
      
      // Convert prices to user's currency for display
      const price = wishItem.price || 0;
      const preorderPrice = wishItem.preorder_price || 0;
      
      const convertedPrice = await convertPriceToUserCurrency(price, wishItem.priceCurrency || 'USD');
      const convertedPreorderPrice = await convertPriceToUserCurrency(preorderPrice, wishItem.preorderPriceCurrency || 'USD');
      
      setFormData({
        ...wishItem,
        price: convertedPrice,
        preorder_price: convertedPreorderPrice,
        priceCurrency: userCurrency,
        preorderPriceCurrency: userCurrency
      });
    } catch (error) {
      console.error('Error loading wish item:', error);
      setError('Failed to load wish item');
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    
    if (!formData.name || !formData.code) {
      console.error('Validation failed: Missing name or code');
      setError('Name and Code are required fields');
      return;
    }

    try {
      setSaving(true);
      setError('');

      // Convert prices to user's currency before saving
      const price = parseFloat(formData.price) || 0;
      const preorderPrice = parseFloat(formData.preorder_price) || 0;
      
      const convertedPrice = await convertPriceToUserCurrency(price, formData.priceCurrency);
      const convertedPreorderPrice = await convertPriceToUserCurrency(preorderPrice, formData.preorderPriceCurrency);

      // Clean and validate form data before submission
      const cleanFormData = {
        ...formData,
        brand: formData.brand || '',
        code: formData.code || '',
        name: formData.name || '',
        series: formData.series || '',
        type: formData.type || '',
        scale: formData.scale || '',
        imageUrl: formData.imageUrl || '',
        price: convertedPrice,
        preorder_price: convertedPreorderPrice,
        priceCurrency: userCurrency,
        preorderPriceCurrency: userCurrency,
        note: formData.note || '-'
      };

      // Simple logic: if id is 'new', create; otherwise, update
      if (isNew) {
        if (!currentUser?.uid) {
          throw new Error('User not authenticated');
        }
        await wishlistService.createWishItem(cleanFormData, currentUser.uid);
      } else {
        if (!id) {
          throw new Error('Wish item ID is required for update');
        }
        await wishlistService.updateWishItem(id, cleanFormData);
      }
      
      navigate('/wishlist');
    } catch (error) {
      console.error('Error saving wish item:', error);
      setError('Failed to save wish item: ' + error.message);
    } finally {
      setSaving(false);
    }
  }

  function handleInputChange(field, value) {
    try {
      setFormData(prev => ({
        ...prev,
        [field]: value === null || value === undefined ? '' : String(value)
      }));
    } catch (error) {
      console.error(`Error in handleInputChange for field ${field}:`, error);
      setFormData(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  }

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/wishlist')}
            sx={{ mb: 2 }}
          >
            Back to Wishlist
          </Button>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            {isNew ? 'Add New Wish Item' : 'Edit Wish Item'}
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Basic Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name *"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Code *"
                value={formData.code}
                onChange={(e) => handleInputChange('code', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={deduplicateArray(masterData.brands)}
                value={formData.brand}
                onChange={(event, newValue) => handleInputChange('brand', newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Brand" fullWidth />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={deduplicateArray(masterData.series)}
                value={formData.series}
                onChange={(event, newValue) => handleInputChange('series', newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Series" fullWidth />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={deduplicateArray(masterData.types)}
                value={formData.type}
                onChange={(event, newValue) => handleInputChange('type', newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Type" fullWidth />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                freeSolo
                options={deduplicateArray(masterData.scales)}
                value={formData.scale}
                onChange={(event, newValue) => handleInputChange('scale', newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Scale" fullWidth />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Image URL"
                value={formData.imageUrl}
                onChange={(e) => handleInputChange('imageUrl', e.target.value)}
                placeholder="https://example.com/image.jpg"
              />
            </Grid>

            {/* Pricing Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
                Pricing Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Price"
                type="number"
                value={formData.price}
                onChange={(e) => handleInputChange('price', e.target.value)}
                InputProps={{
                  startAdornment: <span>{userCurrency} </span>
                }}
              />
            </Grid>





            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.note}
                onChange={(e) => handleInputChange('note', e.target.value)}
                placeholder="Any additional notes about this wish item..."
              />
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/wishlist')}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={16} /> : null}
                >
                  {saving ? 'Saving...' : (isNew ? 'Add Wish Item' : 'Update Wish Item')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
}

export default WishDetail; 